
import { defineComponent, PropType, nextTick } from 'vue';
import type { DropdownItems, DropdownSingleItem } from '@white-label-types/dropdown';
import DropdownList from '../dropdown-list/dropdown-list.vue';
import ErrorMessage from '../error-message/error-message.vue';

export default defineComponent({
  name: 'DropdownWrapper',
  components: {
    DropdownList,
    ErrorMessage,
    IconArrow: () => import('@white-label-icon/icon-arrow'),
  },

  props: {
    dropdownOpened: {
      type: Boolean,
      default: false,
    },
    disableCloseClickingOutside: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array as PropType<DropdownItems>,
      required: true,
    },
    value: {
      type: Object as PropType<DropdownSingleItem>,
      required: true,
    },
    error: {
      type: Boolean,
      default: false,
      required: true,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    errorHtml: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    redErrorMsg: {
      type: Boolean,
      default: false,
    },
    matchWidth: {
      type: Boolean,
      default: false,
    },
    isSuccess: {
      type: Boolean,
      default: false,
    },
    // for when dropdown is a multi-select component and can be searched
    isSearchable: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    localDropdownOpened: {
      get(): boolean {
        return this.dropdownOpened;
      },
      set(val: boolean) {
        this.$emit('update:dropdownOpened', val);
      },
    },

    localValue: {
      get(): string {
        return this.value?.name;
      },

      set(selectedItem: DropdownSingleItem) {
        this.$emit('input', selectedItem);
        this.localDropdownOpened = false;

        this.$nextTick(() => {
          const wrapper = this.$refs.dropdownWrapper as HTMLElement;
          if (wrapper) {
            wrapper.focus();
          }
        })
      }
    },

    scopedSlots() {
      return this.$scopedSlots;
    },
  },

  watch: {
    // important for keyboard navigation - accessibility
    // watcher for shifting focus from wrapper to dropdown-list/customers-selector
    // finds first option that isn't disabled and focuses on it
    localDropdownOpened(isOpen, wasOpen) {
      if(isOpen) {
        if (this.isSearchable) {
          return;
        }

        this.$nextTick(() => {
          let firstOption = this.$el.querySelector('.dropdown-item');

          if (!firstOption) {
            firstOption = this.$el.querySelector<HTMLElement>('.num-selector__button:not(.num-selector__button--disabled), button.button-primary');
          }
          if (firstOption) {
            firstOption.focus();
          }
        })
      // for keeping focus on the dropdown wrapper when option is selected from the dropdown list
      } else if (wasOpen) {
        if (this.$el.contains(document.activeElement)) {
          this.$nextTick(() => {
            const wrapper = this.$refs.dropdownWrapper as HTMLElement;
            if (wrapper) {
              wrapper.focus();
            }
          });
        }
      }
    }
  },

  methods: {
    /**
     * Handles click on the dropdown target.
     * If the dropdown is searchable, it focuses the input field.
     * Otherwise, it toggles the dropdown visibility.
     */
    onClickDropdownTarget(event): void {
      if (this.readonly) {
        return;
      }

      if (this.isSearchable && event.key === 'Enter') {
        const input = this.$refs.dropdownWrapper.querySelector('input');
        if (input) {
          input.focus();
          this.localDropdownOpened = true;
        }
        return;
      }
      this.localDropdownOpened = !this.localDropdownOpened;
    },

    clickOutSide(): void {
      if ((this.isDesktopResolution || this.isSearchable) && !this.disableCloseClickingOutside) {
        this.localDropdownOpened = false;
      }
    },
    //for handling esc key to close dropdown and keep focus on dropdown wrapper for accessibility
    handleEscKey(event: KeyboardEvent): void {
      if(this.localDropdownOpened) {
        event.stopPropagation();
        this.localDropdownOpened = false;

        nextTick(() => {
          const wrapper = this.$refs.dropdownWrapper as HTMLElement;
          if(wrapper) {
            wrapper.focus();
          }
        });
      } else {
        this.$emit('esc-event');
      }
    }
  },
});
